import { IconActivity, IconShield, IconTrendingUp, IconUsers } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {} from "@/components/ui/card";
import { authClient } from "@/lib/auth-client";
import { StatsCard } from "./components/charts/stats-card";
import { AdminHeader } from "./components/shared/admin-header";
import { queryKeys } from "@/lib/query-keys";

async function fetchUserStats() {
  const response = await authClient.admin.listUsers({
    query: {
      limit: 1000,
      sortBy: "createdAt",
      sortDirection: "desc",
    },
  });

  if (!response.data) {
    return { totalUsers: 0, activeUsers: 0, bannedUsers: 0, recentSignups: 0 };
  }

  const users = response.data.users;
  const totalUsers = users.length;
  const bannedUsers = users.filter((user: { banned?: boolean | null }) => user.banned).length;
  const activeUsers = totalUsers - bannedUsers;

  // Count recent signups (last 7 days)
  const RECENT_SIGNUP_DAYS = 7;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - RECENT_SIGNUP_DAYS);
  const recentSignups = users.filter(
    (user: { createdAt: Date }) => new Date(user.createdAt) > sevenDaysAgo,
  ).length;

  return { totalUsers, activeUsers, bannedUsers, recentSignups };
}

export default function AdminAnalytics() {
  const { t } = useTranslation();
  const { data: userStats } = useQuery({
    queryKey: queryKeys.admin.userStats(),
    queryFn: fetchUserStats,
  });

  return (
    <div className="flex flex-col gap-8 p-6">
      <div>
        <AdminHeader
          description={t("admin.analytics.description")}
          title={t("admin.analytics.title")}
        />
      </div>

      {/* User Stats Cards */}
      {userStats && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <StatsCard
            description={t("admin.analytics.totalUsersDesc")}
            icon={<IconUsers className="h-5 w-5" />}
            title={t("admin.analytics.totalUsers")}
            value={userStats.totalUsers.toLocaleString()}
          />
          <StatsCard
            description={t("admin.analytics.activeUsersDesc")}
            icon={<IconActivity className="h-5 w-5" />}
            title={t("admin.analytics.activeUsers")}
            value={userStats.activeUsers.toLocaleString()}
          />
          <StatsCard
            description={t("admin.analytics.newThisWeekDesc")}
            icon={<IconTrendingUp className="h-5 w-5" />}
            title={t("admin.analytics.newThisWeek")}
            value={userStats.recentSignups.toLocaleString()}
          />
          <StatsCard
            description={t("admin.analytics.bannedUsersDesc")}
            icon={<IconShield className="h-5 w-5" />}
            title={t("admin.analytics.bannedUsers")}
            value={userStats.bannedUsers.toLocaleString()}
          />
        </div>
      )}
    </div>
  );
}
